import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './SipSettings.css'

// Same rule as the server (admin.js phoneBookSchema): an optional leading +,
// digits and spaces — "+49 7245 90898066", "+4915159069221" or an extension.
const NUMBER_PATTERN = /^\+?[0-9 ]{3,20}$/

// Rows keep the server id (needed: a Direktanruf page points at a contact by id)
// plus a local key, because new rows have no id until they are saved.
let nextKey = 0
const toRow = (c) => ({
  key:        `k${nextKey++}`,
  id:         c.id ?? null,
  name:       c.name ?? '',
  sip_target: c.sip_target ?? '',
  enabled:    c.enabled !== false,
})

// One phone book per tenant (migration 038). Before that this page edited a
// separate list for every station the tenant was assigned to.
const SipSettings = () => {
  const { apiFetch, isOwner, admin } = useAuth()

  const [tenants,        setTenants]        = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [contacts,       setContacts]       = useState([])
  // { used, maxAttempts, usage: [{ flowLabel, stepNumber, contactId, enabled }] }
  const [directCall,     setDirectCall]     = useState(null)
  const [attemptsDraft,  setAttemptsDraft]  = useState(2)
  const [savingAttempts, setSavingAttempts] = useState(false)
  const [loading,        setLoading]        = useState(false)
  const [dirty,          setDirty]          = useState(false)
  const [saving,         setSaving]         = useState(false)
  const [error,          setError]          = useState(null)
  const [success,        setSuccess]        = useState(false)

  // Owner: load tenant list for picker
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants').then(r => r.json()).then(data => {
        const list = Array.isArray(data) ? data : []
        setTenants(list)
        if (list.length > 0) setSelectedTenant(list[0].id)
      }).catch(() => {})
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner])

  const applyResponse = (data) => {
    setContacts((Array.isArray(data?.contacts) ? data.contacts : []).map(toRow))
    setDirectCall(data?.directCall ?? null)
    setAttemptsDraft(data?.directCall?.maxAttempts ?? 2)
    setDirty(false)
  }

  const loadContacts = useCallback(async () => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    setSuccess(false)
    try {
      const res = await apiFetch(`/api/admin/sip-contacts/${selectedTenant}`)
      if (!res.ok) throw new Error()
      applyResponse(await res.json())
    } catch {
      setError('Laden fehlgeschlagen')
    } finally {
      setLoading(false)
    }
    // apiFetch is deliberately not a dependency: AuthContext recreates it on every
    // provider render (e.g. a token refresh), and reloading would throw away edits.
  }, [selectedTenant])

  useEffect(() => { loadContacts() }, [loadContacts])

  const edit = (updater) => {
    setContacts(updater)
    setDirty(true)
    setSuccess(false)
  }

  const updateContact = (key, field, value) =>
    edit(prev => prev.map(c => c.key === key ? { ...c, [field]: value } : c))

  const addContact = () =>
    edit(prev => [...prev, toRow({})])

  const removeContact = (key) =>
    edit(prev => prev.filter(c => c.key !== key))

  const moveContact = (idx, delta) =>
    edit(prev => {
      const target = idx + delta
      if (target < 0 || target >= prev.length) return prev
      const next = [...prev]
      ;[next[idx], next[target]] = [next[target], next[idx]]
      return next
    })

  const save = async () => {
    if (!selectedTenant) return
    if (contacts.some(c => !c.name.trim() || !c.sip_target.trim())) {
      setError('Alle Felder ausfüllen oder leere Kontakte entfernen.')
      return
    }
    const badNumber = contacts.find(c => !NUMBER_PATTERN.test(c.sip_target.trim()))
    if (badNumber) {
      setError(`Ungültige Rufnummer „${badNumber.sip_target}“ — erlaubt sind Ziffern, Leerzeichen und ein führendes +.`)
      return
    }

    setSaving(true)
    setError(null)
    try {
      const res = await apiFetch(`/api/admin/sip-contacts/${selectedTenant}`, {
        method: 'PUT',
        body: JSON.stringify({
          contacts: contacts.map(c => ({
            ...(c.id && { id: c.id }),
            name:       c.name.trim(),
            sip_target: c.sip_target.trim(),
            enabled:    c.enabled,
          })),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen')
      applyResponse(data)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveAttempts = async () => {
    setSavingAttempts(true)
    setError(null)
    try {
      const res = await apiFetch(`/api/admin/sip-contacts/${selectedTenant}/direct-call-settings`, {
        method: 'PUT',
        body: JSON.stringify({ maxAttempts: Number(attemptsDraft) }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen')
      setDirectCall(prev => ({ ...prev, maxAttempts: data.maxAttempts }))
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2000)
    } catch (e) {
      setError(e.message)
    } finally {
      setSavingAttempts(false)
    }
  }

  // Where each saved contact is called from automatically. New rows have no id yet,
  // so nothing can reference them.
  const usageFor = (contactId) =>
    contactId ? (directCall?.usage || []).filter(u => u.contactId === contactId) : []

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Telefonkontakte</h1>
          <p>Anruf-Kontakte für das Kiosk – gelten für alle Stationen des Mandanten</p>
        </div>
      </div>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Gespeichert</div>}

      {isOwner && tenants.length > 0 && (
        <div className="card sip-tenant-card">
          <div className="form-group">
            <label>Mandant</label>
            <select className="form-control" value={selectedTenant}
              onChange={e => setSelectedTenant(e.target.value)}>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      )}

      <div className="card">
        <p className="sm-hint">
          Die Rufnummern sind im Kiosk nicht sichtbar — nur der Name wird angezeigt.
          Inaktive Kontakte werden am Kiosk ausgeblendet, bleiben aber gespeichert.
        </p>

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : (
          <table className="sip-contacts-table">
            <thead>
              <tr>
                <th className="sip-col-order"></th>
                <th>Name</th>
                <th>Rufnummer</th>
                <th className="sip-col-active">Aktiv</th>
                {directCall?.used && <th>Direktanruf</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c, idx) => (
                <tr key={c.key} className={c.enabled ? '' : 'sip-row--inactive'}>
                  <td className="sip-col-order">
                    <div className="sip-order-btns">
                      <button className="btn btn-ghost btn-sm" title="Nach oben"
                        disabled={idx === 0} onClick={() => moveContact(idx, -1)}>↑</button>
                      <button className="btn btn-ghost btn-sm" title="Nach unten"
                        disabled={idx === contacts.length - 1} onClick={() => moveContact(idx, 1)}>↓</button>
                    </div>
                  </td>
                  <td>
                    <input className="form-control" placeholder="z. B. Empfang"
                      value={c.name}
                      onChange={e => updateContact(c.key, 'name', e.target.value)} />
                  </td>
                  <td>
                    <input className="form-control" placeholder="z. B. +4912345678"
                      value={c.sip_target}
                      onChange={e => updateContact(c.key, 'sip_target', e.target.value)} />
                  </td>
                  <td className="sip-col-active">
                    <input type="checkbox" checked={c.enabled}
                      onChange={e => updateContact(c.key, 'enabled', e.target.checked)} />
                  </td>
                  {directCall?.used && (
                    <td className="sip-usage">
                      {usageFor(c.id).map(u => (
                        <div key={`${u.flowType}-${u.stepNumber}`}>
                          {u.flowLabel}, Schritt {u.stepNumber}{u.enabled ? '' : ' (deaktiviert)'}
                        </div>
                      ))}
                      {usageFor(c.id).length > 0 && !c.enabled && (
                        <div className="sip-usage-warn">Inaktiv — diese Seite ruft niemanden an</div>
                      )}
                    </td>
                  )}
                  <td className="sip-actions">
                    {/* The server refuses too; disabling here just says so before saving. */}
                    <button className="btn btn-ghost btn-sm sip-btn-delete"
                      disabled={usageFor(c.id).length > 0}
                      title={usageFor(c.id).length > 0
                        ? 'Wird von einem Direktanruf verwendet — zuerst im Flow Builder einen anderen Kontakt wählen'
                        : ''}
                      onClick={() => removeContact(c.key)}>
                      Entfernen
                    </button>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && (
                <tr><td colSpan={directCall?.used ? 6 : 5} className="sip-empty">Noch keine Kontakte</td></tr>
              )}
            </tbody>
          </table>
        )}

        <div className="sip-footer">
          <button className="btn btn-ghost" onClick={addContact} disabled={loading}>
            + Kontakt hinzufügen
          </button>
          <div className="sip-footer-right">
            {dirty && <span className="sip-unsaved">Ungespeicherte Änderungen</span>}
            <button className="btn btn-primary" onClick={save} disabled={saving || loading || !dirty}>
              {saving ? <><span className="spinner spinner-sm" /> Speichern…</> : 'Speichern'}
            </button>
          </div>
        </div>
      </div>

      {/* Only once a flow of this tenant actually contains a Direktanruf page. */}
      {directCall?.used && !loading && (
        <div className="card sip-direct-call-card">
          <div className="section-title">Direktanruf</div>
          <div className="sip-attempts-row">
            <label htmlFor="sip-attempts">Anrufversuche</label>
            <select id="sip-attempts" className="form-control sip-attempts-select"
              value={attemptsDraft}
              onChange={e => setAttemptsDraft(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button className="btn btn-primary" onClick={saveAttempts}
              disabled={savingAttempts || attemptsDraft === directCall.maxAttempts}>
              {savingAttempts ? <><span className="spinner spinner-sm" /> Speichern…</> : 'Speichern'}
            </button>
          </div>
          <p className="sm-hint">
            Wie oft ein Besucher anrufen kann, wenn niemand abnimmt, bevor er gebeten wird, es später
            erneut zu versuchen. Gilt für alle Direktanruf-Seiten dieses Mandanten.
          </p>
        </div>
      )}

      {selectedTenant && (
        <VersionHistoryPanel table="sip_contacts" tenantId={selectedTenant} onRestored={loadContacts} />
      )}
    </div>
  )
}

export default SipSettings
